import {firstValueFrom, timeout} from 'rxjs';

import {Inject, Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SwPush} from '@angular/service-worker';
import {REMIT_NOTIFICATION_VAPID_PUBLIC_KEY} from './config';
import {WebPushSubscriptionService} from './web-push-subscription.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  constructor(
    private swPush: SwPush,
    private snackBar: MatSnackBar,
    private webPushSubscriptionService: WebPushSubscriptionService,
    @Inject(REMIT_NOTIFICATION_VAPID_PUBLIC_KEY) private vapidPublicKey: string,
  ) {}

  /**
   * Check if push notifications are supported
   */
  get isSupported(): boolean {
    return this.swPush.isEnabled;
  }

  /**
   * Check if user is already subscribed
   */
  async isSubscribed(): Promise<boolean> {
    if (!this.swPush.isEnabled) {
      return false;
    }

    try {
      // Use firstValueFrom with timeout to prevent hanging
      const subscription = await firstValueFrom(
          this.swPush.subscription.pipe(
              timeout(5000), // 5 second timeout
          ),
      );

      const isSubscribed = !!subscription;

      return isSubscribed;
    } catch (error) {
      return false;
    }
  }

  /**
   * Subscribe to push notifications
   */
  async subscribeToNotifications(): Promise<boolean> {
    if (!this.swPush.isEnabled) {
      this.showMessage('❌ Push notifications are not supported in this browser');
      return false;
    }

    try {
      // Request notification permission
      const permission = await this.requestPermission();
      if (!permission) {
        this.showMessage('❌ Notification permission denied');
        return false;
      }

      // Subscribe to push notifications
      const subscription = await this.swPush.requestSubscription({
        serverPublicKey: this.vapidPublicKey,
      });

      // Send subscription to backend
      await this.sendSubscriptionToBackend(subscription);

      this.showMessage('✅ Successfully subscribed to Remit notifications!');
      return true;
    } catch (error) {
      console.error('Subscription failed:', error);
      this.showMessage('❌ Failed to subscribe to notifications');
      return false;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribeFromNotifications(): Promise<boolean> {
    if (!this.swPush.isEnabled) {
      return false;
    }

    try {
      const subscription = await firstValueFrom(
          this.swPush.subscription.pipe(
              timeout(5000),
          ),
      );

      if (subscription) {
        await subscription.unsubscribe();

        this.showMessage('✅ Successfully unsubscribed from notifications');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Unsubscription failed:', error);
      this.showMessage('❌ Failed to unsubscribe from notifications');
      return false;
    }
  }

  /**
   * Request notification permission
   */
  private async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  private async sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
    const subscriptionData = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')),
        auth: this.arrayBufferToBase64(subscription.getKey('auth')),
      },
      appType: 'pay88' as const,
    };

    await firstValueFrom(
        this.webPushSubscriptionService.createSubscription(subscriptionData),
    );
  }


  private arrayBufferToBase64(buffer: ArrayBuffer | null): string {
    if (!buffer) {
      return '';
    }
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}
